import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { Card, CardTitle } from '@components/common/Card';
import { Button } from '@components/common/Button';
import type { AllowedSsoUser, DirectoryUser, SsoConnectionStatus } from '@schemas/api';

const STATUS_LABELS: Record<string, string> = {
  pending_consent: 'Pending admin consent',
  connected: 'Connected',
  error: 'Connection error',
  disconnected: 'Not connected',
};

export interface SsoProviderCardProps {
  /** e.g. "Microsoft 365 SSO" / "Google Workspace SSO" */
  title: string;
  /** e.g. "Let users in your organization sign in with their Microsoft 365 account." */
  description: string;
  /** e.g. "Microsoft 365" / "Google Workspace" — used in loading/error/button copy */
  providerLabel: string;
  /** e.g. "Microsoft 365 directory" / "Google Workspace directory" */
  directoryLabel: string;

  statusQuery: UseQueryResult<SsoConnectionStatus>;
  connectMutation: UseMutationResult<{ admin_consent_url: string }, unknown, void, unknown>;
  disconnectMutation: UseMutationResult<void, unknown, void, unknown>;

  search: string;
  onSearchChange: (value: string) => void;
  directoryUsersQuery: UseQueryResult<DirectoryUser[]>;

  allowedUsersQuery: UseQueryResult<AllowedSsoUser[]>;
  setAllowedUsersMutation: UseMutationResult<AllowedSsoUser[], unknown, string[], unknown>;
  revokeAllowedUserMutation: UseMutationResult<void, unknown, string, unknown>;
}

export const SsoProviderCard = ({
  title,
  description,
  providerLabel,
  directoryLabel,
  statusQuery,
  connectMutation,
  disconnectMutation,
  search,
  onSearchChange,
  directoryUsersQuery,
  allowedUsersQuery,
  setAllowedUsersMutation,
  revokeAllowedUserMutation,
}: SsoProviderCardProps) => {
  const { data: status, isLoading: statusLoading, isError: statusError } = statusQuery;
  const isConnected = status?.status === 'connected';

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const {
    data: directoryUsers,
    isLoading: directoryLoading,
    isError: directoryError,
  } = directoryUsersQuery;

  const {
    data: allowedUsers,
    isLoading: allowedLoading,
    isError: allowedError,
  } = allowedUsersQuery;

  const handleConnect = () => {
    connectMutation.mutate(undefined, {
      onSuccess: ({ admin_consent_url }) => {
        window.location.href = admin_consent_url;
      },
    });
  };

  const toggleSelected = (externalUserId: string) => {
    setSelectedIds((current) =>
      current.includes(externalUserId)
        ? current.filter((id) => id !== externalUserId)
        : [...current, externalUserId]
    );
  };

  const handleGrantAccess = () => {
    if (selectedIds.length === 0) {
      return;
    }
    setAllowedUsersMutation.mutate(selectedIds, {
      onSuccess: () => setSelectedIds([]),
    });
  };

  return (
    <Card>
      <div className="p-6 space-y-6">
        <div>
          <CardTitle>{title}</CardTitle>
          <p className="text-sm text-content-secondary">{description}</p>
        </div>

        {statusLoading && (
          <p className="text-sm text-content-secondary">Loading connection status…</p>
        )}
        {statusError && (
          <p className="text-sm text-red-600 dark:text-red-400">
            Failed to load {providerLabel} connection status.
          </p>
        )}

        {!statusLoading && !statusError && status && (
          <>
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                status.status === 'connected'
                  ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : status.status === 'error'
                  ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  : status.status === 'pending_consent'
                  ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              }`}
            >
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  status.status === 'connected'
                    ? 'bg-green-500'
                    : status.status === 'error'
                    ? 'bg-red-500'
                    : status.status === 'pending_consent'
                    ? 'bg-yellow-500 animate-pulse'
                    : 'bg-gray-400'
                }`}
              />
              {STATUS_LABELS[status.status] ?? status.status}
              {status.external_domain ? ` — ${status.external_domain}` : ''}
            </div>

            <div className="flex items-center gap-3">
              {!isConnected && (
                <Button
                  variant="primary"
                  onClick={handleConnect}
                  disabled={connectMutation.isPending}
                >
                  {connectMutation.isPending ? 'Connecting…' : `Connect ${providerLabel}`}
                </Button>
              )}
              {isConnected && (
                <Button
                  variant="danger"
                  onClick={() => disconnectMutation.mutate()}
                  disabled={disconnectMutation.isPending}
                >
                  {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
                </Button>
              )}
            </div>
          </>
        )}

        {isConnected && (
          <div className="space-y-4 border-t border-gray-200 pt-6 dark:border-gray-700">
            <div>
              <h3 className="text-lg font-semibold text-content-primary">Grant sign-in access</h3>
              <p className="text-sm text-content-secondary">
                Search your {directoryLabel} and select the users who should be allowed to
                sign in.
              </p>
            </div>

            <input
              type="text"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search directory by name or email"
              className="input w-full"
            />

            {directoryLoading && (
              <p className="text-sm text-content-secondary">Loading directory users…</p>
            )}
            {directoryError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to load directory users.
              </p>
            )}
            {!directoryLoading && !directoryError && (directoryUsers?.length ?? 0) === 0 && (
              <p className="text-sm text-content-secondary">No directory users found.</p>
            )}

            {!directoryLoading && !directoryError && (directoryUsers?.length ?? 0) > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {directoryUsers!.map((user) => (
                    <li key={user.external_user_id} className="flex items-center gap-3 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={user.already_allowed || selectedIds.includes(user.external_user_id)}
                        disabled={user.already_allowed}
                        onChange={() => toggleSelected(user.external_user_id)}
                        className="h-4 w-4"
                        aria-label={`Select ${user.email}`}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-content-primary">
                          {user.display_name || user.email}
                        </div>
                        {user.display_name && (
                          <div className="text-xs text-content-secondary">{user.email}</div>
                        )}
                      </div>
                      {user.already_allowed && (
                        <span className="text-xs font-medium text-content-secondary">
                          Already allowed
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              variant="primary"
              onClick={handleGrantAccess}
              disabled={selectedIds.length === 0 || setAllowedUsersMutation.isPending}
            >
              {setAllowedUsersMutation.isPending
                ? 'Granting access…'
                : `Grant sign-in access to selected users${
                    selectedIds.length > 0 ? ` (${selectedIds.length})` : ''
                  }`}
            </Button>

            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <h3 className="mb-2 text-lg font-semibold text-content-primary">
                Users with sign-in access
              </h3>

              {allowedLoading && (
                <p className="text-sm text-content-secondary">Loading allowed users…</p>
              )}
              {allowedError && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  Failed to load allowed users.
                </p>
              )}
              {!allowedLoading && !allowedError && (allowedUsers?.length ?? 0) === 0 && (
                <p className="text-sm text-content-secondary">
                  No users have been granted sign-in access yet.
                </p>
              )}

              {!allowedLoading && !allowedError && (allowedUsers?.length ?? 0) > 0 && (
                <div className="overflow-x-auto border-y border-gray-200 dark:border-gray-700">
                  <table className="w-full text-left text-sm">
                    <thead className="text-xs uppercase text-content-secondary">
                      <tr>
                        <th className="px-2 py-2">User</th>
                        <th className="px-2 py-2">Status</th>
                        <th className="w-12 px-2 py-2">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {allowedUsers!.map((user) => {
                        const revokePending =
                          revokeAllowedUserMutation.isPending &&
                          revokeAllowedUserMutation.variables === user.id;
                        return (
                          <tr key={user.id} className="border-t border-gray-100 dark:border-gray-800">
                            <td className="px-2 py-2">
                              <div className="font-medium text-content-primary">
                                {user.display_name || user.email}
                              </div>
                              {user.display_name && (
                                <div className="text-xs text-content-secondary">{user.email}</div>
                              )}
                            </td>
                            <td className="px-2 py-2 capitalize">{user.status}</td>
                            <td className="px-2 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => revokeAllowedUserMutation.mutate(user.id)}
                                disabled={revokePending}
                                className="rounded p-2 text-content-muted hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-950/40"
                                title="Revoke sign-in access"
                                aria-label={`Revoke access for ${user.email}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
